import React, { useState, useEffect } from "react";
import axios from "axios";
import { DollarSign, Users, Calendar, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Analytics = () => {
  const [analytics, setAnalytics] = useState({
    total_guests: 0,
    revenue_saved: 0,
    total_reservations: 0,
    daily_reservations: [],
    busiest_hours: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass-card p-3 rounded-lg border border-[#22C55E]/30">
          <p className="font-mono text-sm text-[#22C55E]">{payload[0].value} reservations</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-[#27272A] bg-[#18181B]/80 backdrop-blur-md">
        <div className="px-8 py-6">
          <h1 className="text-4xl font-bold tracking-tight font-mono mb-2">Analytics & ROI</h1>
          <p className="text-sm text-[#A1A1AA]">Business performance metrics</p>
        </div>
      </header>

      <div className="flex-1 overflow-auto scroll-container p-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#22C55E]"></div>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="grid grid-cols-3 gap-6">
              <div className="glass-card p-6 rounded-lg neon-border">
                <div className="flex items-center gap-4">
                  <DollarSign className="w-12 h-12 text-[#22C55E]" />
                  <div>
                    <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Revenue Saved</p>
                    <p data-testid="revenue-saved" className="text-3xl font-bold font-mono text-[#22C55E]">${analytics.revenue_saved.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6 rounded-lg">
                <div className="flex items-center gap-4">
                  <Users className="w-12 h-12 text-[#3B82F6]" />
                  <div>
                    <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Total Guests</p>
                    <p data-testid="total-guests" className="text-3xl font-bold font-mono text-[#3B82F6]">{analytics.total_guests}</p>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6 rounded-lg">
                <div className="flex items-center gap-4">
                  <Calendar className="w-12 h-12 text-[#F59E0B]" />
                  <div>
                    <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Total Reservations</p>
                    <p data-testid="total-reservations" className="text-3xl font-bold font-mono text-[#F59E0B]">{analytics.total_reservations}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-6 rounded-lg">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp className="w-6 h-6 text-[#22C55E]" />
                <h2 className="text-2xl font-semibold font-mono">Reservations per Day (Last 30 Days)</h2>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.daily_reservations}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis
                      dataKey="date"
                      stroke="#A1A1AA"
                      style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      stroke="#A1A1AA"
                      style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#22C55E" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="glass-card p-6 rounded-lg">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp className="w-6 h-6 text-[#3B82F6]" />
                <h2 className="text-2xl font-semibold font-mono">Busiest Hours</h2>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.busiest_hours}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis
                      dataKey="hour"
                      stroke="#A1A1AA"
                      style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
                    />
                    <YAxis
                      stroke="#A1A1AA"
                      style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#3B82F6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;